import type {
  ReclaimAccessDefaults,
  ReclaimAccessSource,
  ReclaimCampaignRule,
  ReclaimCommercialTermsDisputes,
} from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import {
  DEFAULT_RECLAIM_ACCESS_DEFAULTS,
  resolveReclaimCommercialTerms,
} from "@redotech/redo-dispute-settings-common/reclaim-access-settings";

export const RECLAIM_ONBOARDING_V2_PRODUCT_ID = "chargebacks";
export const RECLAIM_SETUP_GUIDE_PRODUCT_ID = "disputes";
export const RECLAIM_ONBOARDING_V2_SOURCE = "merchant_setup_guide";
export const RECLAIM_ONBOARDING_V2_META_KEY = "_reclaimSetup";
export const RECLAIM_PROJECTED_WIN_RATE = 0.6;
export const RECLAIM_DEAL_SIZE_VOLUME_WINDOW = "year";
export const RECLAIM_DEAL_SIZE_WIN_RATE_SOURCE = "projected_reclaim_target";

export const RECLAIM_ONBOARDING_V2_STEPS = [
  {
    setupStepId: "welcome",
    onboardingV2StepId: "cb-in-app-welcome",
    label: "Welcome",
  },
  {
    setupStepId: "partner-code",
    onboardingV2StepId: "cb-in-app-partner-code",
    label: "Partner Code",
  },
  {
    setupStepId: "finalInfo",
    onboardingV2StepId: "cb-in-app-trial-terms",
    label: "Commercial Terms",
  },
  {
    setupStepId: "shopify-permissions",
    onboardingV2StepId: "cb-in-app-shopify-permissions",
    label: "Shopify Permissions",
  },
  {
    setupStepId: "activated",
    onboardingV2StepId: "cb-in-app-activated",
    label: "Activated",
  },
] as const;

export type ReclaimSetupGuideStepId =
  (typeof RECLAIM_ONBOARDING_V2_STEPS)[number]["setupStepId"];
export type ReclaimOnboardingV2StepId =
  (typeof RECLAIM_ONBOARDING_V2_STEPS)[number]["onboardingV2StepId"];

type ReclaimOnboardingV2Step = (typeof RECLAIM_ONBOARDING_V2_STEPS)[number];

const STEP_BY_SETUP_ID: ReadonlyMap<string, ReclaimOnboardingV2Step> = new Map(
  RECLAIM_ONBOARDING_V2_STEPS.map((step) => [step.setupStepId, step]),
);

const STEP_BY_ONBOARDING_V2_ID: ReadonlyMap<string, ReclaimOnboardingV2Step> =
  new Map(
    RECLAIM_ONBOARDING_V2_STEPS.map((step) => [step.onboardingV2StepId, step]),
  );

export const RECLAIM_ONBOARDING_V2_TOTAL_STEPS =
  RECLAIM_ONBOARDING_V2_STEPS.length;

export interface ReclaimCommercialTerms {
  accessMode: ReclaimAccessDefaults["accessMode"] | "paid";
  trialMonths: number;
  successFeePercentage: number;
  minFeeDollars: number;
}

export interface ReclaimCommercialTermsCopy {
  announcementDescription: string;
  body: string;
  cta: string;
  metricStat: string;
  metricLabel: string;
  insightSubtitleWithData: string;
  insightSubtitleWithoutData: string;
  metricFallbackNote: string;
  metricNote: string;
  successBillingPoint: string;
  title: string;
}

export type ReclaimTrialTerms = ReclaimCommercialTerms;
export type ReclaimTrialTermsCopy = ReclaimCommercialTermsCopy;

export type ReclaimDealSizingStatus = "computed" | "skipped" | "unavailable";

export type ReclaimDealSizingReason =
  | "missing_account_id"
  | "invalid_account_id"
  | "query_failed"
  | "multi_currency";

export interface ReclaimDealSizingCurrencyBreakdown {
  currency: string;
  projectedDealSize: number;
  trailingYearChargebackVolume: number;
}

export interface ReclaimDealSizingMetadata {
  calculatedAt: string;
  currencyBreakdown: ReclaimDealSizingCurrencyBreakdown[];
  dealSize?: number;
  feePercentage: number;
  feeRate: number;
  feeSource: "team_settings" | "default";
  projectedWinRate: number;
  reason?: ReclaimDealSizingReason;
  selectedCurrency?: string;
  status: ReclaimDealSizingStatus;
  volumeWindow: typeof RECLAIM_DEAL_SIZE_VOLUME_WINDOW;
  winRateSource: typeof RECLAIM_DEAL_SIZE_WIN_RATE_SOURCE;
}

export interface ReclaimDealSizingMirrorInput {
  dealSize: number | null;
  metadata: ReclaimDealSizingMetadata;
}

export function getReclaimCommercialTerms({
  accessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
  campaign,
  disputes,
  source,
}: {
  accessDefaults?: ReclaimAccessDefaults;
  campaign?: ReclaimCampaignRule;
  disputes?: ReclaimCommercialTermsDisputes | null;
  source?: ReclaimAccessSource;
} = {}): ReclaimCommercialTerms {
  return resolveReclaimCommercialTerms({
    accessDefaults,
    campaign,
    disputes,
    source,
  });
}

export function getReclaimCommercialTermsCopy({
  accessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
  campaign,
  disputes,
  source,
}: {
  accessDefaults?: ReclaimAccessDefaults;
  campaign?: ReclaimCampaignRule;
  disputes?: ReclaimCommercialTermsDisputes | null;
  source?: ReclaimAccessSource;
} = {}): ReclaimCommercialTermsCopy {
  const terms = getReclaimCommercialTerms({
    accessDefaults,
    campaign,
    disputes,
    source,
  });

  if (terms.accessMode === "free") {
    return {
      title: "Free Reclaim access, no billing required",
      body: "Reclaim defends your disputes for free. No billing is required.",
      cta: "Turn on Reclaim",
      announcementDescription:
        "Let Reclaim fight chargebacks automatically for free. No billing required.",
      insightSubtitleWithData:
        "Turn on Reclaim and we'll fight every chargeback for you, automatically. Free access, no billing required.",
      insightSubtitleWithoutData:
        "Every chargeback costs you time and money. Reclaim fights them for you automatically. Free access, no billing required.",
      metricFallbackNote: "Free access, no billing required",
      metricNote: "No billing required",
      metricStat: "Free",
      metricLabel: "Reclaim access",
      successBillingPoint: "Free access is enabled; no billing required",
    };
  }

  if (terms.accessMode === "paid") {
    const pricingText = formatPricingTerms(terms);
    return {
      title: "Billing required to turn on Reclaim",
      body: `Reclaim defends your disputes automatically. Redo charges ${pricingText} only on money we win back.`,
      cta: "Turn on Reclaim",
      announcementDescription: `Let Reclaim fight chargebacks automatically. Redo charges ${pricingText} only on money we win back.`,
      insightSubtitleWithData: `Turn on Reclaim and we'll fight every chargeback for you, automatically. Redo charges ${pricingText} only on money we win back.`,
      insightSubtitleWithoutData: `Every chargeback costs you time and money. Reclaim fights them for you automatically. Redo charges ${pricingText} only on money we win back.`,
      metricFallbackNote: `${terms.successFeePercentage}% success fee`,
      metricNote: "Billing required",
      metricStat: `${terms.successFeePercentage}%`,
      metricLabel: "success fee",
      successBillingPoint: `You'll only pay when we win (${pricingText})`,
    };
  }

  const trialMonthsText = formatMonthCountForSentence(terms.trialMonths);
  const pricingText = formatPricingTerms(terms);
  return {
    title: `${terms.trialMonths}-month free trial, no billing required`,
    body: `Reclaim defends your disputes free for ${trialMonthsText}. After the trial, Redo charges ${pricingText} only on money we win back.`,
    cta: `Start your ${terms.trialMonths}-month free trial`,
    announcementDescription: `Let Reclaim fight chargebacks automatically with a ${terms.trialMonths}-month free trial. No billing required; after the trial, Redo charges ${pricingText} only on money we win back.`,
    insightSubtitleWithData: `Turn on Reclaim and we'll fight every chargeback for you, automatically. Start with a ${terms.trialMonths}-month free trial, no billing required. After the trial, Reclaim charges ${pricingText} only on money we win back.`,
    insightSubtitleWithoutData: `Every chargeback costs you time and money. Reclaim fights them for you automatically. Start with a ${terms.trialMonths}-month free trial, no billing required. After the trial, Reclaim charges ${pricingText} only on money we win back.`,
    metricFallbackNote: `${terms.trialMonths}-month free trial, then ${terms.successFeePercentage}% only on wins`,
    metricNote: `No billing required; then ${terms.successFeePercentage}% only on wins`,
    metricStat: `${terms.trialMonths} mo`,
    metricLabel: "free trial",
    successBillingPoint: `Your trial is active; after it ends, you'll only pay when we win (${pricingText})`,
  };
}

export function getReclaimTrialTerms(
  accessDefaults: ReclaimAccessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
  source?: ReclaimAccessSource,
): ReclaimTrialTerms {
  return getReclaimCommercialTerms({ accessDefaults, source });
}

export function getReclaimTrialTermsCopy(
  accessDefaults: ReclaimAccessDefaults = DEFAULT_RECLAIM_ACCESS_DEFAULTS,
  source?: ReclaimAccessSource,
): ReclaimTrialTermsCopy {
  return getReclaimCommercialTermsCopy({ accessDefaults, source });
}

function formatPricingTerms({
  successFeePercentage,
}: {
  successFeePercentage: number;
}): string {
  return `a ${successFeePercentage}% success fee`;
}

function formatMonthCountForSentence(months: number): string {
  if (months === 6) {
    return "six months";
  }
  return `${months} months`;
}

export function getReclaimOnboardingV2StepId(
  setupStepId: string,
): ReclaimOnboardingV2StepId | null {
  return STEP_BY_SETUP_ID.get(setupStepId)?.onboardingV2StepId ?? null;
}

export function getReclaimOnboardingV2StepLabel(
  onboardingV2StepId: string,
): string | null {
  return STEP_BY_ONBOARDING_V2_ID.get(onboardingV2StepId)?.label ?? null;
}

export function isReclaimOnboardingV2StepId(stepId: string): boolean {
  return STEP_BY_ONBOARDING_V2_ID.has(stepId);
}

export function isReclaimOnboardingV2MirrorFormData(
  formData: unknown,
): boolean {
  if (!formData || typeof formData !== "object") {
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const meta = (formData as Record<string, unknown>)[
    RECLAIM_ONBOARDING_V2_META_KEY
  ];
  if (!meta || typeof meta !== "object") {
    return false;
  }
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (meta as Record<string, unknown>).source === RECLAIM_ONBOARDING_V2_SOURCE
  );
}
